// SPDX-License-Identifier: AGPL-3.0-or-later

import {useSegmentedSquash} from '@app/features/ui/konvo_controls/KonvoControls';
import styles from '@app/features/ui/segmented_tabs/SegmentedTabs.module.css';
import {getNextTabIndex, getTabNavigationDirection} from '@app/features/ui/tabs/TabKeyboardNavigation';
import {clsx} from 'clsx';
import type React from 'react';
import {useRef} from 'react';

export interface SegmentedTab<T extends string = string> {
	id: T;
	label: string;
}

interface SegmentedTabsProps<T extends string = string> {
	tabs: Array<SegmentedTab<T>>;
	selectedTab: T;
	onTabChange: (tab: T) => void;
	ariaLabel?: string;
	className?: string;
}

export function SegmentedTabs<T extends string = string>({
	tabs,
	selectedTab,
	onTabChange,
	ariaLabel,
	className,
}: SegmentedTabsProps<T>) {
	const tabRefs = useRef(new Map<T, HTMLButtonElement>());
	const selectedIndex = tabs.findIndex((tab) => tab.id === selectedTab);
	const moving = useSegmentedSquash(selectedTab);
	const focusTab = (tabId: T) => {
		tabRefs.current.get(tabId)?.focus();
	};
	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, tabId: T) => {
		const currentIndex = tabs.findIndex((tab) => tab.id === tabId);
		if (currentIndex < 0) return;
		const direction = getTabNavigationDirection(event.key, 'horizontal');
		if (!direction) return;
		const nextIndex = getNextTabIndex(currentIndex, tabs.length, direction);
		if (nextIndex == null) return;
		event.preventDefault();
		event.stopPropagation();
		const nextTab = tabs[nextIndex];
		if (!nextTab) return;
		onTabChange(nextTab.id);
		focusTab(nextTab.id);
	};
	return (
		<div className={clsx(styles.container, className)} data-flx="ui.segmented-tabs.segmented-tabs.container">
			<div
				className={styles.tabList}
				role="tablist"
				aria-label={ariaLabel}
				aria-orientation="horizontal"
				data-flx="ui.segmented-tabs.segmented-tabs.tab-list"
			>
				{tabs.map((tab) => {
					const isSelected = selectedTab === tab.id;
					return (
						<button
							key={tab.id}
							ref={(element) => {
								if (element) {
									tabRefs.current.set(tab.id, element);
								} else {
									tabRefs.current.delete(tab.id);
								}
							}}
							type="button"
							role="tab"
							aria-selected={isSelected}
							tabIndex={isSelected ? 0 : -1}
							onClick={() => onTabChange(tab.id)}
							onKeyDown={(event) => handleKeyDown(event, tab.id)}
							className={clsx(styles.tab, isSelected ? styles.tabActive : styles.tabInactive)}
							data-flx="ui.segmented-tabs.segmented-tabs.tab.button"
						>
							{tab.label}
						</button>
					);
				})}
				{/* KONVO: the glass lens of the kit's segmented control; it slides on a
				    CSS spring and squashes while it moves (reduced motion turns both off). */}
				<span
					className={clsx(styles.tabBackground, moving && styles.tabBackgroundMoving)}
					style={{
						width: `calc((100% - 8px) / ${tabs.length} + 2px)`,
						left: `calc(3px + (100% - 8px) * ${selectedIndex} / ${tabs.length})`,
					}}
					aria-hidden="true"
					data-flx="ui.segmented-tabs.segmented-tabs.tab-background"
				/>
			</div>
		</div>
	);
}
